import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import type { ViewAsRole } from '../../auth/context/AuthContext';
import { parseJwt } from '../../auth';
import { EffectiveLevel } from '../../../types/backend';
import { LEVEL_LABELS } from '../../../shared/constants/authorizationGroups';

/** View-as previews a level only (X-OpsPilot-View-As); the backend refuses any other value. */
export const VIEW_AS_OPTIONS: { role: ViewAsRole; label: string; description: string }[] = [
  { role: EffectiveLevel.L1, label: LEVEL_LABELS[EffectiveLevel.L1], description: 'Reads, and works at L1 on every customer' },
  { role: EffectiveLevel.L2, label: LEVEL_LABELS[EffectiveLevel.L2], description: 'May escalate to L2 on a customer' },
  { role: EffectiveLevel.ADMIN, label: LEVEL_LABELS[EffectiveLevel.ADMIN], description: 'May request admin on a customer, and approve admin requests' },
];

export function useUserProfile(open: boolean) {
  const { user, idToken, isRealAdmin, viewAsRole, setViewAsRole, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = useMemo(() => {
    const t: { key: string; label: string }[] = [{ key: 'profile', label: 'Profile' }];
    if (isRealAdmin) t.push({ key: 'viewas', label: 'View As' });
    t.push({ key: 'session', label: 'Session' });
    return t;
  }, [isRealAdmin]);

  useEffect(() => {
    if (open) setActiveTab('profile');
  }, [open]);

  const tokenData = useMemo(() => {
    if (!idToken) return null;
    return parseJwt(idToken);
  }, [idToken]);

  const sessionInfo = useMemo(() => {
    if (!tokenData) return null;

    const loginTime = tokenData.auth_time
      ? new Date(tokenData.auth_time * 1000).toLocaleString()
      : 'N/A';
    const expiresAt = tokenData.exp
      ? new Date(tokenData.exp * 1000).toLocaleString()
      : 'N/A';

    let timeRemaining = 'N/A';
    if (tokenData.exp) {
      const remaining = tokenData.exp * 1000 - Date.now();
      if (remaining > 0) {
        const minutes = Math.floor(remaining / 60000);
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        timeRemaining = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      } else {
        timeRemaining = 'Expired';
      }
    }

    return { loginTime, expiresAt, timeRemaining };
  }, [tokenData]);

  const assignedLevelLabel = user?.assignedLevel ? LEVEL_LABELS[user.assignedLevel] : 'No level granted';
  const viewAsLabel = VIEW_AS_OPTIONS.find((o) => o.role === viewAsRole)?.label ?? viewAsRole;

  return {
    user,
    isRealAdmin,
    viewAsRole,
    setViewAsRole,
    logout,
    activeTab,
    setActiveTab,
    tabs,
    sessionInfo,
    assignedLevelLabel,
    viewAsLabel,
  };
}
